'use client';

import { Clock } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { Availability } from '@/types/tournament.model';
import { formatDateForUser } from '@/utils/formatters';
import { cn } from '@/lib/utils';

interface GridAvailabilityPopoverProps {
  team1Label: string;
  team2Label: string;
  team1Availability: Availability[];
  team2Availability: Availability[];
  /**
   * The side has no pair yet. In a group of 4's dependent match its availability is the window the
   * two possible pairs have in common; in a bracket slot it is always empty.
   */
  team1Undecided: boolean;
  team2Undecided: boolean;
  /** The date the card sits on, highlighted in both lists. */
  date: string;
}

/**
 * Both pairs' declared availability, read-only, straight from the card.
 *
 * The same information already lives in the fine-grained modal, but "Editar" is disabled while a
 * drag-and-drop draft is pending — exactly when the organizer is rearranging matches and needs to
 * know when each pair can play. This never writes anything, so it stays available throughout.
 *
 * Deliberately not `AvailabilitySection`: that one splits each day into morning/afternoon buckets
 * sized for a modal, and doesn't fit in a popover. Here a day is just its list of ranges.
 */
export function GridAvailabilityPopover({
  team1Label,
  team2Label,
  team1Availability,
  team2Availability,
  team1Undecided,
  team2Undecided,
  date,
}: GridAvailabilityPopoverProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 text-gray-500 hover:text-gray-900"
          title="Ver la disponibilidad de las parejas"
        >
          <Clock className="h-3 w-3" />
          Horarios
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3 p-3 text-xs" align="start">
        <TeamAvailability
          label={team1Label}
          availability={team1Availability}
          undecided={team1Undecided}
          date={date}
        />
        <TeamAvailability
          label={team2Label}
          availability={team2Availability}
          undecided={team2Undecided}
          date={date}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * An empty list doesn't mean the pair declared nothing: the backend trims availability to the
 * grid's dates, and an undecided side comes back empty whenever there's no pair to read it from (a
 * bracket slot, or a dependent match where one of the two possible pairs loaded nothing). The copy
 * says only what the empty list does mean.
 */
function TeamAvailability({
  label,
  availability,
  undecided,
  date,
}: {
  label: string;
  availability: Availability[];
  undecided: boolean;
  date: string;
}) {
  const days = [...availability].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-1">
      <p className="truncate font-semibold text-gray-900">{label}</p>
      {undecided && days.length > 0 && (
        <p className="text-gray-500">Horarios que tienen en común las dos parejas posibles.</p>
      )}
      {days.length === 0 ? (
        <p className="text-gray-500">
          {undecided
            ? 'Todavía no se sabe qué pareja juega acá.'
            : 'No cargó horarios para las fechas de la grilla.'}
        </p>
      ) : (
        <ul className="space-y-0.5">
          {days.map((day) => {
            const ranges = day.timeRange ?? [];
            return (
              <li
                key={day.date}
                className={cn(
                  'flex gap-2 rounded px-1 py-0.5',
                  day.date === date && 'bg-sky-50 font-medium',
                )}
              >
                <span className="w-16 shrink-0 text-gray-500">
                  {formatDateForUser(day.date, 'EEE dd/MM')}
                </span>
                <span className="text-gray-800">
                  {ranges.length === 0
                    ? 'Sin horarios'
                    : ranges
                        .map((r) => `${r.startTime}–${r.endTime}`)
                        .join(', ')}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
